/**
 * Hints above the documents list (owner decisions, October 4, 2026; canvas "Tour on their own
 * job + tips"). For someone who said No thanks to the tour, or met the app another way: one
 * hint at a time, under the export line, at the moment it can help. A hint has no label row, no
 * glow, no Next and no progress; its X closes it.
 *
 * Settings › Appearance has one Show hints checkbox (Brandy, October 5), and it is never checked
 * while hints are off ("the checkbox should never be checked if we've turned it off"). It is
 * checked while at least one hint can still appear: hints are on, and not every hint has been
 * closed (its X) or retired (its own rule once it has done its job: the Needs review hint, at
 * their first confirmation of a waiting document outside a tour; the speed test hint, once a
 * speed test has finished on this computer). Unchecking it turns every hint off; checking it
 * turns hints on and brings them all back, so each shows again when its own moment comes (the
 * speed test hint only while no test has finished).
 *
 * They are called hints (owner, October 4): "tips" are the setup screen's cards and the email
 * sign-up, a different thing, and "tooltips" are the labels on buttons. Two hints reuse the
 * wording of a setup screen tip (SETUP_TIPS), which stays a tip there.
 *
 * Every number a hint shows is a measured count: waiting now, or the engine's review outcomes
 * from its correction ledger (documents that waited this week and were confirmed unchanged;
 * the latest documents the operator changed). Nothing is estimated. The speed test hint shows
 * no number: it is for a computer that has never been measured.
 */
import type { ReviewOutcomes } from '../shared/desktop-api';

export type HintId = 'first-review' | 'computer-check' | 'assisted' | 'let-it-run';
/** Every hint, in the order they are checked. */
export const HINT_IDS: readonly HintId[] = [
  'first-review',
  'computer-check',
  'assisted',
  'let-it-run',
];

/** This computer's speed test: whether one has ever finished here, and whether one runs now. */
export type SpeedTestFacts = { finished: boolean; running: boolean };
/** A job's pilot mode as the hints see it: Manual or Autopilot (Autopilot.tsx pilotMode). */
export type PilotMode = 'human-reviewed' | 'ai-reviewed';

export type HintFacts = {
  /** Documents waiting in Needs review now. */
  waiting: number;
  /** A tour runs, or the tour is being offered: neither competes with a hint. */
  touring: boolean;
  offering: boolean;
  /** A sample job (the tour's, or a ready-made one). */
  sample: boolean;
  /** This computer can run Autopilot's AI check (it calls in a smarter AI when it's unsure). */
  assistedAvailable: boolean;
  pilot: PilotMode;
  /**
   * The job is in Calibration (owner, 2026-10-31): Calibration is recommended to complete before Autopilot, so no
   * hint that opens Pilot mode (the second look, Let it run) shows until it ends.
   */
  calibrating?: boolean;
  /** The job's export sends by itself as documents are ready. */
  sendsByItself: boolean;
  /** The engine's review outcomes for this job; null until read. */
  review: ReviewOutcomes | null;
  /** This computer's speed tests; null until read (the speed test hint waits for it). */
  speedTest?: SpeedTestFacts | null;
  /** Settings › Appearance › Show hints wasn't unchecked. */
  enabled: boolean;
  /** Hints closed with their X, or retired by their own rule once they have done their job. */
  closed: readonly string[];
  /** The setup screen's "Let it run" tip (SETUP_TIPS), whose words the Let it run hint reuses. */
  letItRun?: { headline: string; description: string };
  /** The setup screen's AI check tip ("An expert second look, built in"), whose words the second-look hint reuses. */
  secondLook?: { headline: string; description: string };
};

/** Autopilot's AI check is worth a look once this many documents waited this week… */
export const ASSISTED_WAITED = 10;
/** …and this share of them was confirmed without any change (a closer look from a smarter AI would have done). */
export const ASSISTED_UNCHANGED_SHARE = 0.8;
/** Autopilot is worth a look once this many of the latest documents went through unchanged. */
export const LET_IT_RUN_RECENT = 100;

/**
 * The one hint to show now, or none. In order: first review, the speed test, the second look, Let it run.
 * The last two both suggest moving a Manual job to Autopilot (two pilot modes since October 19, 2026): the
 * second look where Autopilot's AI check runs, for what waited and was confirmed unchanged; Let it run for a
 * job whose latest documents all went through unchanged. Neither shows for a job already on Autopilot, nor for one
 * still in Calibration (owner, 2026-10-31).
 * The second look keeps its id "assisted", so a hint someone closed stays closed.
 *
 * First review shows whenever documents wait, also after a tour (Brandy, October 5: "just
 * getting them started, I think that's a good thing"); it retires itself once they confirm a
 * waiting document outside a tour (firstReviewDone), until Show hints is checked again.
 *
 * The speed test hint (owner, October 6, 2026) shows once nothing waits in Needs review, while
 * no speed test has ever finished on this computer and none runs. A finished test retires it
 * for good: checking Show hints again doesn't bring it back.
 */
export function hintToShow(facts: HintFacts): HintId | null {
  if (!facts.enabled || facts.touring || facts.offering || facts.sample) return null;
  const open = (id: HintId) => !facts.closed.includes(id);
  const review = facts.review;
  if (open('first-review') && facts.waiting > 0) return 'first-review';
  if (
    open('computer-check') &&
    facts.waiting === 0 &&
    facts.speedTest &&
    !facts.speedTest.finished &&
    !facts.speedTest.running
  )
    return 'computer-check';
  if (
    open('assisted') &&
    facts.assistedAvailable &&
    facts.pilot === 'human-reviewed' &&
    !facts.calibrating &&
    review &&
    review.waitedThisWeek >= ASSISTED_WAITED &&
    review.waitedConfirmedUnchanged >= review.waitedThisWeek * ASSISTED_UNCHANGED_SHARE
  )
    return 'assisted';
  if (
    open('let-it-run') &&
    facts.pilot === 'human-reviewed' &&
    !facts.calibrating &&
    facts.sendsByItself &&
    facts.waiting === 0 &&
    review &&
    review.recentDocuments >= LET_IT_RUN_RECENT &&
    review.recentChanged === 0
  )
    return 'let-it-run';
  return null;
}

type ListedDocument = {
  id: string;
  needsReview?: boolean | null;
  confirmedAsWhole?: boolean | null;
};

/** The documents waiting in Needs review now, by id. */
export const waitingDocumentIds = (documents: readonly ListedDocument[]): Set<string> =>
  new Set(
    documents
      .filter((document) => document.needsReview && !document.confirmedAsWhole)
      .map((document) => document.id),
  );

/**
 * The first-review hint has done its job: a document that was waiting (`before`) is now confirmed
 * as a whole. Only on their own job and outside a tour, whose confirmations are the tour's (the
 * hint keeps showing after a tour). A document that merely left the list (exported, removed)
 * confirms nothing.
 */
export function firstReviewDone(
  before: ReadonlySet<string>,
  documents: readonly ListedDocument[],
  { sample, touring }: { sample: boolean; touring: boolean },
): boolean {
  if (sample || touring) return false;
  return documents.some(
    (document) => before.has(document.id) && document.confirmedAsWhole === true,
  );
}

const count = (n: number, one: string, many: string) =>
  n === 1 ? `1 ${one}` : `${n.toLocaleString('en-US')} ${many}`;

/** What each hint says. `**words**` are the product's own names, in bold. */
export const HINT_WORDS: Record<
  HintId,
  {
    title: (facts: HintFacts) => string;
    text: (facts: HintFacts) => string;
    action?: 'pilot-mode' | 'speed-test';
  }
> = {
  // The hint they need after No thanks: confirm what waits for review in the list. It retires once they have.
  'first-review': {
    title: ({ waiting }) =>
      waiting === 1
        ? '1 document needs your review'
        : `${count(waiting, 'document', 'documents')} need your review`,
    text: () =>
      "Cortexa wasn't sure of something on these, so they wait for you in the list, without the green check; each one says why at the top right. Check each one and click **Confirm & next**. Confirming lets it export, and teaches Paige your documents.",
  },
  // A computer never measured (owner, October 6, 2026; its own words since Brandy's rewording that
  // day, no longer the setup screen's "What can this computer do?" tip headline). Run speed test
  // opens This computer › Performance and starts it there.
  'computer-check': {
    title: () => 'Want to process documents faster?',
    text: () =>
      'Find out what this computer is capable of. Run a 3-minute **speed test** and learn how to make things faster.',
    action: 'speed-test',
  },
  // The setup screen's AI check tip, after the counts (owner, October 4: it "calls in a smarter AI to
  // take a closer look"; the old "AI double-check" undersold it). Its button opens Pilot mode, for Autopilot.
  assisted: {
    title: ({ secondLook }) => secondLook?.headline ?? 'An expert second look, built in',
    text: ({ review, secondLook }) =>
      `This week ${count(review?.waitedThisWeek ?? 0, 'document', 'documents')} waited in **Needs review**, and you confirmed ${(review?.waitedConfirmedUnchanged ?? 0).toLocaleString('en-US')} of them without changing anything. ${secondLook?.description ?? ''}`.trim(),
    action: 'pilot-mode',
  },
  // The setup screen's "Let it run" tip, at the moment it applies (owner: reuse it).
  'let-it-run': {
    title: ({ letItRun }) => letItRun?.headline ?? 'Let it run',
    text: ({ review, letItRun }) =>
      `Your last ${count(review?.recentDocuments ?? 0, 'document', 'documents')} went through without a single change from you. ${letItRun?.description ?? ''}`.trim(),
    action: 'pilot-mode',
  },
};

// ---------------------------------------------------------------------------
// What is kept on this computer: the Show hints checkbox, and the hints closed or retired
// ---------------------------------------------------------------------------

/** Hints closed with their X, or retired by their own rule (kept under its October 4 name). */
export const HINTS_CLOSED_KEY = 'cpv6-hints-closed';
/** Settings › Appearance › Show hints: "off" turns every hint off. */
export const SHOW_HINTS_KEY = 'cpv6-show-hints';
export const HINTS_EVENT = 'capturepoint-hints';

type HintStorage = Pick<Storage, 'getItem' | 'setItem'>;
export type HintSettingsState = { enabled: boolean; closed: string[] };

/**
 * Whether hints are on, and the hints closed or retired, each once. Read as kept by every version:
 * the per-hint checkboxes of October 5 (00c7180a) kept an unchecked hint on the same list as a
 * closed one and left this switch on, so any hint they left on still shows the box checked, and
 * all of them off shows it clear, with nothing to convert.
 */
export function readHintSettings(
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): HintSettingsState {
  let enabled = true;
  try {
    enabled = storage?.getItem(SHOW_HINTS_KEY) !== 'off';
  } catch {
    /* Read as on. */
  }
  let closed: string[] = [];
  try {
    const value = JSON.parse(storage?.getItem(HINTS_CLOSED_KEY) ?? '[]');
    if (Array.isArray(value))
      closed = [...new Set(value.filter((id): id is string => typeof id === 'string'))];
  } catch {
    /* A broken record closes nothing. */
  }
  return { enabled, closed };
}

/**
 * The hints that can show on this computer: the second look only where Autopilot's AI check runs, and the speed test
 * hint only while no speed test has finished here (not while that is still being read).
 */
export const hintsThatCanShow = (
  assistedAvailable: boolean,
  speedTest: SpeedTestFacts | null = null,
): HintId[] =>
  HINT_IDS.filter(
    (id) =>
      (id !== 'assisted' || assistedAvailable) &&
      (id !== 'computer-check' || (speedTest !== null && !speedTest.finished)),
  );

/**
 * What the Show hints checkbox shows: checked only while a hint can still appear. Hints are on,
 * and at least one of the hints this computer can show hasn't been closed or retired. When the
 * last one goes, by its X or its own rule, the box clears by itself (Brandy, October 5).
 */
export function showHintsChecked(
  state: HintSettingsState,
  assistedAvailable: boolean,
  speedTest: SpeedTestFacts | null = null,
): boolean {
  return (
    state.enabled &&
    hintsThatCanShow(assistedAvailable, speedTest).some((id) => !state.closed.includes(id))
  );
}

/**
 * The Show hints checkbox. Unchecking turns every hint off. Checking turns hints on and brings
 * them all back: none is closed or retired any more, so each shows again when its own moment
 * comes (the Needs review hint, once documents wait; the speed test hint only while no test has
 * finished on this computer, which its own rule keeps).
 */
export function setShowHints(
  show: boolean,
  storage: HintStorage | undefined = globalThis.localStorage,
): void {
  try {
    if (show) storage?.setItem(HINTS_CLOSED_KEY, '[]');
    storage?.setItem(SHOW_HINTS_KEY, show ? 'on' : 'off');
  } catch {
    /* It stays as it was; the box shows what is kept. */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(HINTS_EVENT));
}

/**
 * Its X, or its own rule once it has done its job: that hint is closed until Show hints is checked again. True when
 * this closed it (it wasn't closed already), so a rule retiring it is reported once.
 */
export function closeHint(
  id: HintId,
  storage: HintStorage | undefined = globalThis.localStorage,
): boolean {
  const { closed } = readHintSettings(storage);
  if (closed.includes(id)) return false;
  try {
    storage?.setItem(HINTS_CLOSED_KEY, JSON.stringify([...closed, id]));
  } catch {
    /* It may show again. */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(HINTS_EVENT));
  return true;
}
